<script setup>
import { getReservedStockUploadDetail } from '@/api/reserved-stock'
import { ref, computed, getCurrentInstance, reactive } from 'vue'
import dayjs from 'dayjs'
import { i18n } from '@/i18n'
import { inventoryQueryClientRequest } from '@/api/inventory'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  searchText: {
    type: String,
    default: '',
  },
  warehouseHash: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['download', 'view-detail'])

const isOpen = ref(false)
const fetchingChildren = ref(false)
const children = ref([])
const innerPagination = reactive({
  current: 1,
  pageSize: 10,
  total: props.item.total_rows || 0,
})

const expandedRowKeys = ref([])
const expandedComponents = reactive({})
const expandedLoading = reactive({})

async function fetchComponents(record) {
  const productId = record.product_id || record.catalog_id
  if (!productId || expandedComponents[record.id]) return
  
  expandedLoading[record.id] = true
  try {
    const { data } = await inventoryQueryClientRequest({
      method: 'GET',
      url: `/components/${productId}`,
      params: {
        warehouse_id: record.warehouse_id,
      },
      headers: {
        'Business-Id': record.business_id,
      },
    })
    expandedComponents[record.id] = data.data || []
  } catch (err) {
    console.error('Failed to fetch components:', err)
  } finally {
    expandedLoading[record.id] = false
  }
}

function handleExpand(expanded, record) {
  if (expanded) {
    expandedRowKeys.value = [...expandedRowKeys.value, record.id]
    fetchComponents(record)
  } else {
    expandedRowKeys.value = expandedRowKeys.value.filter(key => key !== record.id)
  }
}

function isSoftBundling(record) {
  return record.bundling_type === 'SOFT' || record.is_bundling === 1 || record.is_bundling === true
}

const innerColumns = [
  {
    title: i18n.t('reservedStock.sku_product') || 'SKU / Product',
    key: 'sku_product',
    scopedSlots: { customRender: 'sku_product' },
    width: '30%',
  },
  {
    title: i18n.t('reservedStock.warehouse') || 'Warehouse',
    key: 'warehouse',
    scopedSlots: { customRender: 'warehouse' },
    width: '15%',
  },
  {
    title: i18n.t('reservedStock.value') || 'Value',
    key: 'value',
    scopedSlots: { customRender: 'value' },
    width: '15%',
    align: 'right',
  },
  {
    title: i18n.t('reservedStock.reason_msg') || 'Reason / Msg',
    key: 'reason',
    scopedSlots: { customRender: 'reason' },
    width: '25%',
  },
  {
    title: i18n.t('reservedStock.status') || 'Status',
    key: 'status',
    scopedSlots: { customRender: 'status' },
    align: 'center',
    width: '15%',
  },
]

async function fetchChildren() {
  fetchingChildren.value = true
  try {
    const { data } = await getReservedStockUploadDetail(props.item.process_id, {
      page: innerPagination.current,
      size: innerPagination.pageSize,
    })
    children.value = data.content || data.data || []
    innerPagination.total = data.total_of_elements ?? data.totalElements ?? data.totalRow ?? props.item.total_rows
  } catch (err) {
    console.error('Failed to fetch history details:', err)
  } finally {
    fetchingChildren.value = false
  }
}

function handleInnerTableChange(p) {
  innerPagination.current = p.current
  innerPagination.pageSize = p.pageSize
  fetchChildren()
}

function toggleChildren() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    fetchChildren()
  }
}

const { $notification } = getCurrentInstance().proxy

function handleCopy(text) {
  if (!text) return
  navigator.clipboard.writeText(text).then(() => {
    $notification.success({
      message: i18n.t('reservedStock.copied') || 'Copied!',
      description: i18n.t('reservedStock.copied_desc', { sku: text }) || `SKU ${text} copied to clipboard`,
      duration: 2,
    })
  }).catch(err => {
    console.error('Failed to copy text: ', err)
  })
}

function getStatusColor(status) {
  switch (status) {
    case 'SUCCESS':
    case 'COMPLETED': return 'green'
    case 'FAILED': return 'red'
    case 'ONPROGRESS': return 'blue'
    default: return 'gray'
  }
}

function highlightText(text) {
  if (!props.searchText || !text) return text
  const regex = new RegExp(`(${props.searchText.trim()})`, 'gi')
  return String(text).replace(regex, '<mark style="background-color: #fffb8f; padding: 0; border-radius: 2px; color: rgba(0, 0, 0, 0.85);">$1</mark>')
}

function formatDate(date) {
  if (!date) return '-'
  return dayjs(date).format('DD MMM YYYY HH:mm:ss')
}

const canDownload = computed(() => {
  return ['COMPLETED', 'SUCCESS', 'FAILED'].includes(props.item.status)
})
</script>

<template>
  <tbody :class="{ 'is-open': isOpen }">
    <tr class="parent-row" @click="toggleChildren">
      <!-- Column 1: Info (Time, User, Status) -->
      <td>
        <div class="batch-audit-info">
          <div class="d-flex align-items-center mb-1">
            <a-icon :type="isOpen ? 'caret-down' : 'caret-right'" class="mr-2 text-primary" style="font-size: 14px" />
            <span class="date-time">{{ formatDate(item.started_at) }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 pl-4">
            <div class="user-pill">
              <a-icon type="user" class="mr-1" style="font-size: 10px" />
              <span>{{ item.user_name || item.created_by_name || 'System' }}</span>
            </div>
            <a-tag :color="getStatusColor(item.status)" class="m-0 mini-tag">
              {{ item.status }}
            </a-tag>
          </div>
        </div>
      </td>

      <!-- Column 2: Progress -->
      <td>
        <div class="batch-stats-container">
          <div class="mini-progress">
            <div class="mini-progress__bar success" :style="{ width: ((item.success_count / (item.total_rows || 1)) * 100) + '%' }" />
            <div class="mini-progress__bar failed" :style="{ width: ((item.failed_count / (item.total_rows || 1)) * 100) + '%' }" />
          </div>
          <div class="small mt-1 text-muted">
            {{ item.success_count }}/{{ item.total_rows }}
          </div>
        </div>
      </td>

      <!-- Column 3: Action -->
      <td class="text-right">
        <a-button
          type="link"
          size="small"
          class="p-0 font-weight-bold"
          :disabled="!canDownload"
          @click.stop="$emit('download', item.process_id)"
        >
          <a-icon type="cloud-download" style="font-size: 16px" />
          <span class="ml-1">{{ $t('reservedStock.download') }}</span>
        </a-button>
      </td>
    </tr>

    <tr v-if="isOpen" class="expanded-row">
      <td colspan="3" class="p-0">
        <div class="inner-table-container">
          <a-table
            :columns="innerColumns"
            :data-source="children"
            :loading="fetchingChildren"
            :pagination="innerPagination"
            row-key="id"
            size="small"
            :show-header="true"
            :expand-icon-as-cell="false"
            :expand-icon-column-index="-1"
            :expanded-row-keys="expandedRowKeys"
            class="batch-details-table"
            @expand="handleExpand"
            @change="handleInnerTableChange"
          >
            <template slot="expandedRowRender" slot-scope="record">
              <div v-if="expandedLoading[record.id]" class="p-4 text-center">
                <a-spin size="small" :tip="$t('utils.loading') || 'Loading...'" />
              </div>
              <div v-else-if="expandedComponents[record.id]" class="expanded-components-list">
                <div
                  v-for="child in expandedComponents[record.id]"
                  :key="child.id"
                  class="component-row d-flex align-items-center"
                >
                  <div class="inner-col sku-product-col" style="width: 30%">
                    <div class="tree-line" />
                    <div class="tree-dot-child" />
                    <div class="d-flex flex-column">
                      <div class="d-flex align-items-center mb-1">
                        <div class="sku-pill-child">
                          {{ child.sku }}
                        </div>
                        <div class="component-quantity-text">
                          x{{ child.component_quantity }}
                        </div>
                      </div>
                      <div class="product-title-child">
                        {{ child.title }}
                      </div>
                    </div>
                  </div>
                  <div class="inner-col warehouse-col" style="width: 15%">
                    <div class="warehouse-name-child">
                      {{ child.warehouse_name || '-' }}
                    </div>
                  </div>
                  <div class="inner-col value-col text-right" style="width: 15%">
                    <span class="value-child">{{ child.availability }}</span>
                  </div>
                  <div class="inner-col reason-col" style="width: 25%" />
                  <div class="inner-col status-col" style="width: 15%" />
                </div>
              </div>
            </template>
            <template slot="sku_product" slot-scope="text, record">
              <div class="sku-info-row d-flex align-items-center">
                <div class="flex-grow-1 overflow-hidden">
                  <div class="d-flex align-items-center mb-1">
                    <div class="sku-pill mr-2">
                      <span v-html="highlightText(record.sku)" />
                      <a-tooltip :title="$t('reservedStock.copy_sku') || 'Copy SKU'">
                        <a-icon
                          type="copy"
                          class="copy-icon"
                          @click.stop="handleCopy(record.sku)"
                        />
                      </a-tooltip>
                    </div>
                    <div v-if="isSoftBundling(record)" class="soft-bundling-tag">
                      {{ $t('productAvailability.soft_bundling') }}
                    </div>
                  </div>
                  <div class="product-title font-weight-bold text-danger" v-html="highlightText(record.catalog_title || record.title)" />
                </div>
              </div>
            </template>
            <template slot="warehouse" slot-scope="text, record">
              <div class="warehouse-name font-weight-bold">
                {{ warehouseHash[record.warehouse_id]?.warehouse_name || record.warehouse_name || record.warehouse_id || '-' }}
              </div>
            </template>

            <template slot="value" slot-scope="text, record">
              <div class="font-weight-bold text-dark" style="font-size: 14px">
                {{ record.new_reserved_stock ?? record.reserved_stock ?? 0 }}
              </div>
            </template>

            <template slot="reason" slot-scope="text, record">
              <div class="reason-container">
                <div class="reason" v-html="highlightText(record.reason || '-')" />
                <div v-if="record.message" class="message" v-html="highlightText(record.message)" />
              </div>
            </template>

            <template slot="status" slot-scope="text, record">
              <a-tag :color="getStatusColor(record.status)" class="m-0 mini-tag">
                {{ record.status }}
              </a-tag>
            </template>
          </a-table>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<style lang="scss" scoped>
.parent-row {
  cursor: pointer;
  background-color: #fff;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f5f7fa;
  }
}

.batch-audit-info {
  .date-time {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
  }
}

.user-pill {
  display: inline-flex;
  align-items: center;
  background: #f0f2f5;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  color: #595959;
  font-weight: 500;
}

.mini-tag {
  font-size: 10px;
  line-height: 1.4;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 700;
  text-transform: uppercase;
  border: none;
}

.batch-stats-container {
  width: 100%;
  max-width: 180px;
}

.batch-stats-summary {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  
  .total { color: #595959; }
  .success { color: #52c41a; }
  .failed { color: #ff4d4f; }
}

.mini-progress {
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  display: flex;
  overflow: hidden;
  
  &__bar {
    height: 100%;
    transition: width 0.5s ease;
    
    &.success { background: #52c41a; }
    &.failed { background: #ff4d4f; }
  }
}

.copy-icon {
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: #1890ff !important;
    background: #e6f7ff;
  }

  &:active {
    transform: scale(0.9);
  }
}

.sku-info {
  .sku {
    font-size: 11px;
    font-weight: 700;
    color: #1890ff;
    letter-spacing: 0.3px;
    background: #e6f7ff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
    margin-bottom: 2px;
  }
  .product-title {
    font-size: 12px;
    font-weight: 500;
    color: #595959;
    display: block;
  }
}

.change-info {
  .movement-pill {
    background: #fff;
    border: 1px solid #e8e8e8;
    padding: 2px 10px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
    
    .old { color: #8c8c8c; font-weight: 400; }
    .new { color: #1890ff; font-weight: 700; }
  }
  .reason {
    font-size: 11px;
    color: #8c8c8c;
    margin-top: 4px;
    font-style: normal;
    padding-left: 6px;
    border-left: 2px solid #f0f0f0;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .name {
    font-size: 12px;
    font-weight: 600;
    color: #595959;
  }
}

.is-child-item {
  transition: all 0.2s;
  
  &:hover {
    background-color: #fafafa !important;
  }

  td {
    padding: 10px 16px;
  }

  td:first-child {
    padding-left: 4.5rem !important;
    position: relative;

    &::before {
      content: '';
      top: 0;
      bottom: 0;
      width: 2px;
      position: absolute;
      left: 2.5rem;
      background: linear-gradient(to bottom, #e8e8e8, #f0f0f0);
    }

    &::after {
      content: '';
      top: 50%;
      width: 18px;
      height: 2px;
      position: absolute;
      left: 2.5rem;
      background: #e8e8e8;
      border-radius: 0 2px 2px 0;
    }

    .tree-dot {
      position: absolute;
      left: calc(2.5rem + 18px);
      top: 50%;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background: #d9d9d9;
      border-radius: 50%;
      z-index: 2;
    }
  }
  
  &:last-child {
     td:first-child::before {
       bottom: 50%;
     }
  }
}

.is-open tr td {
  background-color: #f9fbff;
}

.inner-table-container {
  padding: 0;
  background: #ffffff;

  ::v-deep {
    .ant-table-small {
      border: none !important;
      border-radius: 0 !important;
    }
    
    .ant-table-thead > tr > th {
      background: transparent !important;
      font-size: 11px;
      padding: 8px 16px;
      color: #bfbfbf;
      border-bottom: 1px solid #f0f0f0;
      text-transform: uppercase;
      font-weight: 600;
      
      &:first-child {
        padding-left: 6rem;
      }
    }
    
    .ant-table-tbody > tr > td {
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      background: #ffffff !important;

      &:first-child {
        padding-left: 3.5rem !important;
        position: relative;

        &::before {
          content: '';
          top: 0;
          bottom: 0;
          width: 1px;
          position: absolute;
          left: 1.25rem;
          background-color: rgba(0, 0, 0, 0.1);
          z-index: 1;
        }

        &::after {
          content: '';
          top: 50%;
          width: 1rem;
          height: 1px;
          position: absolute;
          left: 1.25rem;
          background-color: rgba(0, 0, 0, 0.1);
          z-index: 1;
        }
      }
    }
    
    .ant-table-thead > tr > th:first-child {
      padding-left: 3rem !important;
    }
    
    .ant-table-tbody > tr > td:first-child {
      padding-left: 3rem !important;
    }

    .ant-table-tbody > tr:last-child > td:first-child::before {
      bottom: 50%;
    }

    .ant-table-tbody > tr:hover > td {
      background: #f9fcff !important;
    }

    .ant-pagination.ant-table-pagination {
      margin: 16px 24px;
    }
  }
}

.sku-info-row {
  .product-title {
    font-size: 12px;
    line-height: 1.4;
  }
}

.sku-info {
  position: relative;

  .product-title {
    font-size: 13px;
    color: #262626;
    line-height: 1.4;
    margin-bottom: 4px;
  }

  .sku-pill {
    display: inline-flex;
    align-items: center;
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Roboto Mono', monospace;
    font-size: 11px;
    color: #595959;
    border: 1px solid #e8e8e8;
  }
}

.soft-bundling-tag {
  font-size: 10px;
  border: 1px solid rgba(0, 123, 255, 0.8);
  border-radius: 2px;
  padding: 0.5px 4px;
  color: rgba(0, 123, 255, 0.8);
  line-height: 1.1;
  font-weight: 600;
  background: rgba(0, 123, 255, 0.05);
  display: inline-block;
  white-space: nowrap;
}

.expanded-components-list {
  background: #ffffff;
  padding-bottom: 8px;
}

.component-row {
  min-height: 60px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f2f4;
  background: #ffffff;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fcff;
  }

  .inner-col {
    padding: 12px 16px;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .sku-product-col {
    padding-left: 4.5rem;
    position: relative;
    justify-content: flex-start;

    .tree-line {
      content: '';
      top: 0;
      bottom: 0;
      width: 1px;
      position: absolute;
      left: 1.5rem;
      background-color: rgba(0, 0, 0, 0.1);
    }

    .tree-dot-child {
      content: '';
      top: 50%;
      width: 20px;
      height: 1px;
      position: absolute;
      left: 1.5rem;
      background-color: rgba(0, 0, 0, 0.1);
    }

    .sku-pill-child {
      font-size: 11px;
      color: #444444;
      font-family: monospace;
      letter-spacing: 0.025rem;
      background: #f5f5f5;
      padding: 1px 6px;
      border-radius: 2px;
      margin-right: 8px;
    }

    .component-quantity-text {
      font-size: 90%;
      margin-left: auto;
      color: #8c8c8c;
      font-family: monospace;
      letter-spacing: 1px;
    }

    .product-title-child {
      color: #ff4d4f;
      font-weight: 500;
      font-size: 95%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }
  }

  .warehouse-col {
    .warehouse-name-child {
      font-size: 95%;
      color: #262626;
    }
  }

  .value-col {
    justify-content: flex-end;
    .value-child {
      font-weight: 500;
      color: #262626;
    }
  }
}

.component-row:last-child .tree-line {
  bottom: 50%;
}

.component-row:last-child {
  border-bottom: 1px solid #f0f0f0;
  
  .tree-line {
    bottom: 50%;
  }
}

.copy-icon {
  cursor: pointer;
  font-size: 12px;
  color: #bfbfbf;
  transition: all 0.2s;
  margin-left: 6px;

  &:hover {
    color: #1890ff !important;
  }
}

.warehouse-name {
  font-size: 12px;
  color: #595959;
  display: flex;
  align-items: center;
}

.reason-container {
  .reason {
    font-size: 12px;
    color: #595959;
    font-weight: 500;
  }
  .message {
    font-size: 11px;
    color: #8c8c8c;
    font-style: italic;
  }
}

td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
</style>
